import GiftCardSheet from "../components/GiftCardSheet";
import { GiftCardUiModalConfigType } from "../types";
import { useEffect } from "react";
import GiftCardEntryScreen from "./GiftCardEntryScreen";
import { useGiftCardContext } from "../context/GiftProvider";
import GiftCardAddBalance from "./GiftCardAddBalance";
import GiftCardAppliedList from "./GiftCardAppliedList";

export interface GiftCardScreensProps {
  isOpen: boolean;
  onClose: () => void;
  container?: HTMLElement | null;
  accentColor: string;
  uiModalConfig?: GiftCardUiModalConfigType;
  showCloseButton?: boolean;
  /** Formats every amount the screens show. Pass the app's currency formatter. */
  formatAmount?: (value: number) => string;
  isFullyCovered?: boolean;
}

// One sheet for all three screens. `activeStep` swaps what is inside it, and the panel morphs its
// height between them (same FLIP treatment as checkout-ui's discount-achiever tray) rather than the
// sheet sliding out and back in on every step.
const GiftCardScreens = ({
  isOpen,
  onClose,
  container,
  accentColor,
  uiModalConfig,
  showCloseButton,
  formatAmount,
  isFullyCovered,
}: GiftCardScreensProps) => {
  const {
    activeStep,
    setActiveStep,
    activeProvider,
    appliedCards,
    dismissRechargeNotice,
  } = useGiftCardContext();

  useEffect(() => {
    if (!isOpen) return;
    setActiveStep(appliedCards.length > 0 ? "APPLIED_LIST" : "CARD_ENTRY");
    dismissRechargeNotice();
  }, [isOpen]);

  const handleOpenChange = (open: boolean) => {
    if (!open) onClose();
  };

  const renderScreen = () => {
    switch (activeStep) {
      case "WALLET_TOPUP":
        return (
          <GiftCardAddBalance
            iconUrl={activeProvider?.display_config.iconUrl}
            providerLabel={activeProvider?.display_config?.label ?? ""}
            accentColor={accentColor}
            inputType={
              activeProvider?.input_config.inputs[0]?.validation.input_type
            }
            maxLength={activeProvider?.input_config.inputs[0]?.validation.length}
          />
        );
      case "APPLIED_LIST":
        return (
          <GiftCardAppliedList
            formatAmount={formatAmount}
            accentColor={accentColor}
            isFullyCovered={isFullyCovered}
            onSubmit={onClose}
          />
        );
      case "CARD_ENTRY":
      default:
        return (
          <GiftCardEntryScreen
            accentColor={accentColor}
            uiModalConfig={uiModalConfig}
            formatAmount={formatAmount}
          />
        );
    }
  };

  return (
    <GiftCardSheet
      isOpen={isOpen}
      screenKey={activeStep}
      onOpenChange={handleOpenChange}
      container={container}
      showCloseButton={showCloseButton}
      onClose={onClose}
    >
      {renderScreen()}
    </GiftCardSheet>
  );
};

export default GiftCardScreens;
